class ProductDrawer extends HTMLElement {

  constructor() {
    super();

    this.elements = {
      dialog: this.querySelector('[data-product-drawer-el="dialog"]'),
      closeButtons: this.querySelectorAll('[data-product-drawer-el="close-button"]'),
      tabs: this.querySelectorAll('[data-product-drawer-el="tab"]'),
      contents: this.querySelectorAll('[data-product-drawer-el="content"]'),
      deliveryButton: this.querySelector('[data-product-drawer-el="delivery-button"]'),
      deliveryOptionsList: this.querySelector('[data-product-drawer-el="options-list"]'),
      deliveryOptions: this.querySelectorAll('[data-product-drawer-el="option"]'),
      deliveryTitle: this.querySelector('[data-product-drawer-el="delivery-title"]'),
      deliveryText: this.querySelector('[data-product-drawer-el="delivery-text"]'),
      externalDeliveryTitle: document.querySelectorAll('[data-product-drawer-external-el="delivery-title"]'),
      externalDeliveryText: document.querySelectorAll('[data-product-drawer-external-el="delivery-text"]'),
      externalButtons: document.querySelectorAll('[data-product-drawer-external-el="button"]'),
      externalDeliveryMessage: document.querySelectorAll('[data-product-drawer-external-el="delivery-message"]'),
    };

    this.isOpen = false;
    this.currentActiveTab = null;
  }

  connectedCallback() {
    this.setupEventListeners();
    this.initializeFirstTab();
    this.setCorrectDeliveryOption();
    this.checkIfExternalTabShouldBeOpened();
  }

  setupEventListeners() {
    this.elements.closeButtons.forEach(button => {
      button.addEventListener('click', this.closeDrawer.bind(this));
    });

    this.elements.externalButtons.forEach(button => {
      button.addEventListener('click', this.handleExternalButtonClick.bind(this));
    });

    this.elements.dialog.addEventListener('click', (event) => {
      if (event.target === this.elements.dialog) {
        this.closeDrawer();
      }
    });

    if (this.elements.deliveryButton) {
      this.elements.deliveryButton.addEventListener('click', this.showList.bind(this));
    }

    this.elements.deliveryOptions.forEach(option => {
      option.addEventListener('click', this.handleOptionClick.bind(this));
    });

    document.addEventListener('click', this.handleDocumentClick.bind(this));

    this.elements.tabs.forEach(tab => {
      tab.addEventListener('click', this.handleTabClick.bind(this));
    });

    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  async setCorrectDeliveryOption() {
    try {
      const response = await fetch('/browsing_context_suggestions.json');
      const data = await response.json();
      const countryHandle = data?.detected_values?.country?.handle;

      if (countryHandle) {
        const userCountryCode = countryHandle.toUpperCase();
        const matchingOption = Array.from(this.elements.deliveryOptions).find(
          option => option.dataset.isoCode === userCountryCode
        );

        if (matchingOption) {
          matchingOption.click();
          this.showList();
        }
      }
    } catch (error) {
      console.error('Failed to fetch user location:', error);
    } finally {
      this.setDeliveryMessageLoaded();
    }
  }

  setDeliveryMessageLoaded() {
    this.elements.externalDeliveryMessage?.forEach(message => {
      message.dataset.loading = 'false';
    });
  }

  initializeFirstTab() {
    if (this.elements.tabs.length > 0) {
      const firstTab = this.elements.tabs[0];
      const tabId = firstTab.getAttribute('data-tab');
      this.currentActiveTab = tabId;
    }
  }

  handleExternalButtonClick(event) {
    const button = event.currentTarget;
    const targetTabId = button.getAttribute('data-tab');
    const openOptions = button.getAttribute('data-open-options') === 'true';

    if (targetTabId) {
      this.openDrawer();
      this.switchToTab(targetTabId);

      if (openOptions) {
        this.showList();
      }
    }
  }

  handleDocumentClick(event) {
    const target = event.target.closest('[data-product-drawer-external-el="button"]')
    if (target) {
      const targetTabId = target.getAttribute('data-tab');

      if (targetTabId) {
        this.openDrawer();
        this.switchToTab(targetTabId);
      }
    }
  }

  checkIfExternalTabShouldBeOpened() {
    const urlParams = new URLSearchParams(window.location.search);
    const tabToOpen = urlParams.get('product-drawer-tab');

    if (tabToOpen) {
      const validTab = Array.from(this.elements.tabs).some(tab => tab.getAttribute('data-tab') === tabToOpen);

      if (validTab) {
        this.openDrawer();
        this.switchToTab(tabToOpen);
      }
    }
  }

  showList() {
    const isHidden = this.elements.deliveryOptionsList.getAttribute('aria-hidden') === 'true';

    this.elements.deliveryText.setAttribute('aria-hidden', isHidden);

    const openText = this.elements.deliveryButton.getAttribute('data-open-text');
    const closeText = this.elements.deliveryButton.getAttribute('data-close-text');

    this.elements.deliveryButton.textContent = isHidden ? closeText : openText;
    this.elements.deliveryButton.setAttribute('aria-expanded', isHidden);

    this.elements.deliveryOptionsList.setAttribute('aria-hidden', !isHidden);
  }

  handleOptionClick(event) {
    const option = event.currentTarget;
    const title = option.getAttribute('data-title');
    const text = decodeURIComponent(option.getAttribute('data-text').replace(/\+/g, ' '));
    const pdpText = decodeURIComponent(option.getAttribute('data-pdp-text').replace(/\+/g, ' '));

    this.elements.deliveryTitle.textContent = title;
    this.elements.deliveryText.innerHTML = text;
    if (!this.elements.externalDeliveryTitle || !this.elements.externalDeliveryText) return;
    this.elements.externalDeliveryTitle.forEach(el => el.textContent = title);
    this.elements.externalDeliveryText.forEach(el => el.innerHTML = pdpText);

    this.showList();
  }

  handleTabClick(event) {
    const tab = event.currentTarget;
    const tabId = tab.getAttribute('data-tab');

    if (tabId && tabId !== this.currentActiveTab) {
      this.switchToTab(tabId);
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'Escape':
        this.closeDrawer();
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        event.preventDefault();
        this.navigateTabsWithKeyboard(event.key);
        break;
    }
  }

  navigateTabsWithKeyboard(direction) {
    const tabs = Array.from(this.elements.tabs);
    const currentIndex = tabs.findIndex(tab => tab.getAttribute('data-tab') === this.currentActiveTab);

    let nextIndex;
    if (direction === 'ArrowRight') {
      nextIndex = currentIndex + 1 >= tabs.length ? 0 : currentIndex + 1;
    } else {
      nextIndex = currentIndex - 1 < 0 ? tabs.length - 1 : currentIndex - 1;
    }

    const nextTab = tabs[nextIndex];
    const nextTabId = nextTab.getAttribute('data-tab');

    this.switchToTab(nextTabId);
    nextTab.focus();
  }

  switchToTab(tabId) {
    this.elements.tabs.forEach(tab => {
      const isActive = tab.getAttribute('data-tab') === tabId;
      tab.classList.toggle('product-drawer__tab-button--active', isActive);
      tab.setAttribute('aria-selected', isActive);
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.elements.contents.forEach(content => {
      const isActive = content.getAttribute('data-tab') === tabId;
      content.classList.toggle('product-drawer__tab-content--active', isActive);
      content.setAttribute('aria-hidden', !isActive);
    });

    this.currentActiveTab = tabId;
  }

  openDrawer() {
    if (this.isOpen) return;

    this.classList.add('product-drawer--open');
    this.elements.dialog.classList.add('product-dialog--open');
    if (!this.elements.dialog.hasAttribute('open')) {
      this.elements.dialog.showModal();
    }

    const activeTab = this.querySelector('[data-product-drawer-el="tab"][data-tab="' + this.currentActiveTab + '"]');
    if (activeTab) {
      activeTab.focus();
    } else {
      this.focus();
    }

    document.body.style.overflow = 'hidden';

    this.isOpen = true;
  }

  closeDrawer() {
    if (!this.isOpen) return;

    const url = new URL(location.href);
    url.searchParams.delete('product-drawer-tab');
    window.history.replaceState({}, '', url.toString());

    this.classList.remove('product-drawer--open');
    this.elements.dialog.classList.remove('product-dialog--open');
    if (this.elements.dialog.hasAttribute('open')) {
      this.elements.dialog.close();
    }

    const activeButton = document.querySelector('[data-product-drawer-external-el="button"][data-tab="' + this.currentActiveTab + '"]');
    if (activeButton) {
      activeButton.focus();
    }

    document.body.style.overflow = '';

    this.isOpen = false;
  }
}

customElements.define('product-drawer', ProductDrawer);

export default ProductDrawer;
